import type { AutoReconnectConnection } from '../../connection';

/**
 * Execute an IDB operation with automatic retry on reconnectable IDB errors.
 *
 * InvalidStateError means the IDB database handle is stale — closed by
 * a versionchange event (another tab/worker upgraded the DB), HMR in
 * dev mode, or a connection reset. AbortError can surface from the same
 * stale/closed transaction path when a request races a disconnect. The fix:
 *
 * 1. Catch the reconnectable IDB error
 * 2. Signal the connection to reconnect (closes old DB, opens new one)
 * 3. Wait for the reconnection to complete
 * 4. Retry the operation once with the fresh DB handle
 *
 * Without this, the error propagates to the main thread via OpClient
 * and permanently poisons LiveData, making the app unusable.
 */
function isReconnectableIdbError(error: unknown): error is DOMException {
  return (
    error instanceof DOMException &&
    (error.name === 'InvalidStateError' || error.name === 'AbortError')
  );
}

export async function withIdbRetry<T>(
  connection: AutoReconnectConnection,
  operation: () => Promise<T>
): Promise<T> {
  try {
    return await operation();
  } catch (e) {
    if (isReconnectableIdbError(e)) {
      console.warn(`[nbstore:idb] ${e.name} — reconnecting`);
      connection.signalError(new Error(`IDB stale: ${e.message}`));
      await connection.waitForConnected();
      return await operation();
    }
    throw e;
  }
}
